import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { ResumeStrengthMetrics, ResumeScore } from '../../types/resume';

export function useResumeStrength(userId: string) {
  const [metrics, setMetrics] = useState<ResumeStrengthMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResumeStrength();
  }, [userId]);

  const loadResumeStrength = async () => {
    try {
      // Get all scores for the user
      const { data: scores, error } = await supabase
        .from('resume_scores')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!scores || scores.length === 0) {
        setMetrics(null);
        return;
      }

      // Get the latest score
      const latestScore = scores[scores.length - 1];

      // Calculate metrics
      const metrics: ResumeStrengthMetrics = {
        currentScore: latestScore.total_score,
        iterations: scores.length,
        latestFeedback: latestScore.feedback,
        scoreHistory: scores.map(score => ({
          date: score.created_at,
          score: score.total_score
        })),
        criteriaScores: {
          keywords: latestScore.keyword_score,
          formatting: latestScore.format_score,
          content: latestScore.content_score,
          ats: latestScore.ats_score
        }
      };

      setMetrics(metrics);
    } catch (error) {
      console.error('Error loading resume strength:', error);
      toast.error('Failed to load resume metrics');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    metrics,
    isLoading,
    refresh: loadResumeStrength
  };
}